/**
 * Hazards on the client portal map (Diurnal parity 05-025; MB's decisions 82, 83, 625, 765; docs/specs/portal/
 * client-area.md §11). When staff choose "Publish hazards to the client portal" for a project, its client sees the
 * hazards of the project's pilot hazard pack (C5, `hazard-pack.ts`): the same content Oz Runways reads, never a second
 * pipeline, so a pack published again for the pilots reaches the client's map too. The `portal-hazards` Edge Function
 * reads the pack with the service role and hands the client only what this module keeps:
 *
 *   kept         each hazard map (search area, towers, powerlines, other hazards and pins), its BND, the tenant
 *                hazard library's sites
 *   left out     "Survey Lines (n)" and "Survey Boundaries (n)" (the portal draws the plan's own lines and blocks)
 *   landholders  "Pinned Properties (n)" only when staff also share the landholder boundaries (decision 83: boundaries
 *                per project); each property's name and shape, never a description (contacts, notes and flags are
 *                internal, and the pack never holds them either)
 *
 * The pack was checked when it was kept (`checkPackKml`: only Folders, Placemarks, Styles, Points, LineStrings and
 * Polygons), so this reader is a small one for that grammar. Icons are kept only when they are images inside the file
 * (the pins' PNGs); Google Earth icon addresses become plain dots, so the portal fetches nothing from elsewhere. Every
 * name and description is plain text for a text node, never markup.
 *
 * Copied to supabase/functions/_shared/portal-hazards.ts by tools/dev/copy-edge-shared.mjs: it imports nothing.
 */

/** The Edge Function that serves a project's portal hazards. */
export const PORTAL_HAZARDS_FUNCTION = 'portal-hazards';

/** A point on the map. */
export interface PortalLatLng {
  readonly lat: number;
  readonly lng: number;
}

export type PortalHazardShape =
  | { readonly type: 'point'; readonly at: PortalLatLng }
  | { readonly type: 'line'; readonly points: readonly PortalLatLng[] }
  | {
      readonly type: 'polygon';
      readonly outer: readonly PortalLatLng[];
      readonly holes: ReadonlyArray<readonly PortalLatLng[]>;
    };

/** A placemark's look, from its KML style: CSS colours (`rgba(...)`) or null. */
export interface PortalHazardStyle {
  readonly line: string | null;
  readonly width: number;
  readonly fill: string | null;
  /** A point's colour (the KML icon colour), when it has no image of its own. */
  readonly dot: string | null;
  /** A pin's own image (`data:image/png;base64,…`), when the pack carries one. */
  readonly icon: string | null;
  readonly scale: number;
}

export interface PortalHazardPlacemark {
  readonly name: string;
  /** Plain text ('' for landholder properties). */
  readonly description: string;
  /** The folders under the layer it sits in, e.g. "Powerlines › LV". */
  readonly folder: string;
  readonly shape: PortalHazardShape;
  readonly style: PortalHazardStyle;
  /**
   * Marked not present on the recce (R243; `markPlacemarks`): who and when. Set by the reader of the hazard_mark rows,
   * never by the KML, which stays as it was built: only on a hazard already in what this reader is shown.
   */
  readonly mark?: { readonly by: string; readonly at: string };
}

export type PortalHazardKind = 'hazards' | 'landholders';

/** One folder of the pack as the client sees it. */
export interface PortalHazardLayer {
  readonly kind: PortalHazardKind;
  /** The pack's folder name; "Pinned Properties (n)" reads "Landholder properties (n)". */
  readonly name: string;
  readonly placemarks: readonly PortalHazardPlacemark[];
}

export interface PortalHazards {
  readonly layers: readonly PortalHazardLayer[];
  /** The pack's folders the client does not see (for staff: why something is not on the client's map). */
  readonly leftOut: readonly string[];
}

/** The pack's folders the portal draws itself (Teslyn Field's `build_pack` names, crates/teslyn-core hazard_pack.rs). */
const PORTAL_OWN = [/^Survey Lines \(\d+\)$/, /^Survey Boundaries \(\d+\)$/];
const LANDHOLDERS = /^Pinned Properties \((\d+)\)$/;

/** Whether a top folder of the pack is the landholder properties. */
export function isLandholderFolder(name: string): boolean {
  return LANDHOLDERS.test(name.trim());
}

// ------------------------------------------------------------------------------------------------ the reader

interface Node {
  readonly name: string;
  readonly attrs: string;
  readonly children: Node[];
  text: string;
}

const decode = (s: string): string =>
  s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, n: string) => String.fromCodePoint(Number.parseInt(n, 16)))
    .replace(/&amp;/g, '&');

/** The element tree of a checked pack (null when it is not one). */
function readTree(text: string): Node | null {
  const root: Node = { name: '#root', attrs: '', children: [], text: '' };
  const stack: Node[] = [root];
  let i = 0;
  while (i < text.length) {
    const j = text.indexOf('<', i);
    const top = stack[stack.length - 1]!;
    if (j < 0) {
      top.text += decode(text.slice(i));
      break;
    }
    if (j > i) top.text += decode(text.slice(i, j));
    if (text.startsWith('<![CDATA[', j)) {
      const k = text.indexOf(']]>', j + 9);
      if (k < 0) return null;
      top.text += text.slice(j + 9, k);
      i = k + 3;
      continue;
    }
    if (text.startsWith('<!--', j)) {
      const k = text.indexOf('-->', j + 4);
      if (k < 0) return null;
      i = k + 3;
      continue;
    }
    if (text.startsWith('<?', j)) {
      const k = text.indexOf('?>', j + 2);
      if (k < 0) return null;
      i = k + 2;
      continue;
    }
    if (text.startsWith('<!', j)) return null;
    const k = text.indexOf('>', j);
    if (k < 0) return null;
    const tag = text.slice(j + 1, k);
    i = k + 1;
    const m = /^(\/?)([A-Za-z][A-Za-z0-9_.:-]*)([\s\S]*?)(\/?)$/.exec(tag);
    if (!m) return null;
    const name = m[2]!.replace(/^.*:/, '');
    if (m[1]) {
      const open = stack.pop();
      if (!open || open.name !== name || stack.length === 0) return null;
      continue;
    }
    const node: Node = { name, attrs: m[3]!, children: [], text: '' };
    top.children.push(node);
    if (!m[4]) stack.push(node);
  }
  if (stack.length !== 1) return null;
  const kml = root.children.find((c) => c.name === 'kml');
  return kml ?? null;
}

const child = (n: Node, name: string): Node | undefined => n.children.find((c) => c.name === name);
const kids = (n: Node, name: string): Node[] => n.children.filter((c) => c.name === name);
const textOf = (n: Node | undefined): string => (n ? n.text.trim() : '');

/** A KML colour (`aabbggrr`) as CSS, or null. */
export function packColourToCss(kml: string): string | null {
  const h = kml.trim();
  if (!/^[0-9a-fA-F]{8}$/.test(h)) return null;
  const byte = (at: number): number => Number.parseInt(h.slice(at, at + 2), 16);
  return `rgba(${byte(6)}, ${byte(4)}, ${byte(2)}, ${Math.round((byte(0) / 255) * 1000) / 1000})`;
}

interface RawStyle {
  line: string | null;
  width: number | null;
  poly: string | null;
  fill: boolean;
  outline: boolean;
  iconColour: string | null;
  iconHref: string | null;
  scale: number | null;
}

function readStyle(s: Node): RawStyle {
  const line = child(s, 'LineStyle');
  const poly = child(s, 'PolyStyle');
  const icon = child(s, 'IconStyle');
  const num = (n: Node | undefined): number | null => {
    const v = Number.parseFloat(textOf(n));
    return Number.isFinite(v) ? v : null;
  };
  return {
    line: line ? packColourToCss(textOf(child(line, 'color'))) : null,
    width: line ? num(child(line, 'width')) : null,
    poly: poly ? packColourToCss(textOf(child(poly, 'color'))) : null,
    fill: poly ? textOf(child(poly, 'fill')) !== '0' : true,
    outline: poly ? textOf(child(poly, 'outline')) !== '0' : true,
    iconColour: icon ? packColourToCss(textOf(child(icon, 'color'))) : null,
    iconHref: icon ? textOf(child(child(icon, 'Icon') ?? icon, 'href')) || null : null,
    scale: icon ? num(child(icon, 'scale')) : null,
  };
}

/** Only an image inside the file is drawn; the portal fetches no icon from elsewhere. */
const ownImage = (href: string | null): string | null =>
  href && /^data:image\/(png|jpeg|gif);base64,[A-Za-z0-9+/=]+$/.test(href.trim()) ? href.trim() : null;

function coords(text: string): PortalLatLng[] {
  const out: PortalLatLng[] = [];
  for (const t of text.trim().split(/\s+/)) {
    if (!t) continue;
    const [lon, lat] = t.split(',').map((v) => Number.parseFloat(v));
    if (lat === undefined || lon === undefined || !Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) continue;
    out.push({ lat, lng: lon });
  }
  return out;
}

function shapeOf(p: Node): PortalHazardShape | null {
  const point = child(p, 'Point');
  if (point) {
    const at = coords(textOf(child(point, 'coordinates')))[0];
    return at ? { type: 'point', at } : null;
  }
  const line = child(p, 'LineString');
  if (line) {
    const points = coords(textOf(child(line, 'coordinates')));
    return points.length >= 2 ? { type: 'line', points } : null;
  }
  const poly = child(p, 'Polygon');
  if (poly) {
    const ring = (b: Node | undefined): PortalLatLng[] =>
      coords(textOf(child(child(b ?? poly, 'LinearRing') ?? poly, 'coordinates')));
    const outer = ring(child(poly, 'outerBoundaryIs'));
    if (outer.length < 3) return null;
    const holes = kids(poly, 'innerBoundaryIs')
      .map((b) => ring(b))
      .filter((h) => h.length >= 3);
    return { type: 'polygon', outer, holes };
  }
  return null;
}

/** A description as plain text: line breaks kept, any tags dropped (a text node shows the rest as it is). */
export function plainDescription(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .split('\n')
    .map((l) => l.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function styleFor(raw: RawStyle | null): PortalHazardStyle {
  return {
    line: raw?.outline === false ? null : (raw?.line ?? null),
    width: raw?.width ?? 2,
    fill: raw?.fill === false ? null : (raw?.poly ?? null),
    dot: raw?.iconColour ?? null,
    icon: ownImage(raw?.iconHref ?? null),
    scale: raw?.scale ?? 1,
  };
}

/**
 * What the client sees of a pack: its hazards, and its landholder properties when `landholders` (decision 83). Null
 * when the text is not a pack this reader understands.
 */
export function portalHazards(kml: string, opts: { readonly landholders: boolean }): PortalHazards | null {
  const root = readTree(kml);
  const doc = root ? child(root, 'Document') : undefined;
  if (!doc) return null;
  const styles = new Map<string, RawStyle>();
  for (const s of kids(doc, 'Style')) {
    const id = /\sid="([^"]+)"/.exec(s.attrs)?.[1];
    if (id) styles.set(id, readStyle(s));
  }
  const layers: PortalHazardLayer[] = [];
  const leftOut: string[] = [];

  const placemarksOf = (
    folder: Node,
    path: string[],
    landholders: boolean,
    out: PortalHazardPlacemark[],
  ): void => {
    for (const p of kids(folder, 'Placemark')) {
      const shape = shapeOf(p);
      if (!shape) continue;
      const inline = child(p, 'Style');
      const url = textOf(child(p, 'styleUrl')).replace(/^#/, '');
      const raw = inline ? readStyle(inline) : (styles.get(url) ?? null);
      out.push({
        name: textOf(child(p, 'name')),
        description: landholders ? '' : plainDescription(textOf(child(p, 'description'))),
        folder: path.join(' › '),
        shape,
        style: styleFor(raw),
      });
    }
    for (const f of kids(folder, 'Folder'))
      placemarksOf(f, [...path, textOf(child(f, 'name'))], landholders, out);
  };

  // placemarks straight in the document (a pack from another tool): hazards
  const loose: PortalHazardPlacemark[] = [];
  placemarksOf({ ...doc, children: doc.children.filter((c) => c.name === 'Placemark') }, [], false, loose);
  if (loose.length > 0) layers.push({ kind: 'hazards', name: textOf(child(doc, 'name')), placemarks: loose });

  for (const f of kids(doc, 'Folder')) {
    const name = textOf(child(f, 'name'));
    if (PORTAL_OWN.some((r) => r.test(name))) {
      leftOut.push(name);
      continue;
    }
    const property = LANDHOLDERS.exec(name);
    if (property && !opts.landholders) {
      leftOut.push(name);
      continue;
    }
    const placemarks: PortalHazardPlacemark[] = [];
    placemarksOf(f, [], property !== null, placemarks);
    if (placemarks.length === 0) continue;
    layers.push({
      kind: property ? 'landholders' : 'hazards',
      name: property ? `Landholder properties (${property[1]})` : name,
      placemarks,
    });
  }
  return { layers, leftOut };
}

// ------------------------------------------------------------------------------------------------ the request

/** What the portal asks the function: one project. */
export function parsePortalHazardsRequest(
  body: unknown,
): { readonly ok: true; readonly projectId: string } | { readonly ok: false; readonly error: string } {
  const projectId =
    body && typeof body === 'object' ? (body as Record<string, unknown>)['project_id'] : undefined;
  if (
    typeof projectId !== 'string' ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(projectId)
  )
    return { ok: false, error: 'Say which project.' };
  return { ok: true, projectId: projectId.toLowerCase() };
}

/** Whether the client's map shows hazards, and why not. */
export type PortalHazardsState =
  /** Staff have not published hazards to the client portal for this project. */
  | 'not_published'
  /** Published, but no pilot hazard pack has been published for the project yet. */
  | 'no_pack'
  | 'published';

/** The function's answer. */
export interface PortalHazardsAnswer {
  readonly state: PortalHazardsState;
  /** The pack the hazards come from: its name and when it was last published. */
  readonly pack: { readonly name: string; readonly version: number; readonly published_at: string } | null;
  readonly landholders: boolean;
  readonly layers: readonly PortalHazardLayer[];
}

/** How many placemarks each kind of layer holds. */
export function portalHazardCounts(layers: readonly PortalHazardLayer[]): Record<PortalHazardKind, number> {
  const out: Record<PortalHazardKind, number> = { hazards: 0, landholders: 0 };
  for (const l of layers) out[l.kind] += l.placemarks.length;
  return out;
}
